import {
  getAuditRubrics,
  getAuditRubric,
  getAuditRubricsForProject,
  getLiveRubricForProject,
  getAndValidateTaskTemplate,
  createAuditRubric,
  editAuditRubric,
  updateAuditRubricStatus,
  TaskTemplateData,
} from './AuditRubricApi';
import {
  useMutation,
  UseMutationResult,
  useQuery,
  UseQueryResult,
  useQueryClient,
} from '@tanstack/react-query-v4';
import { toast } from 'react-toastify';
import {
  CreateAuditRubricBody,
  EditAuditRubricBody,
  UpdateAuditRubricStatusBody,
  AuditRubric,
  AuditRubricInfo,
  AuditRubricStatus,
  PaginatedAuditRubricInfo,
} from '@scale/shared-types';

const queryFactory = {
  auditRubric: {
    getAuditRubrics: (
      params: {
        status?: AuditRubricStatus[];
        projectId?: string;
        page?: number;
        limit?: number;
        sort?: string;
        sortOrder?: 'asc' | 'desc';
      } = {},
    ) => ({
      queryKey: ['auditRubric', 'all', params] as const,
      queryFn: () => getAuditRubrics(params),
    }),
    getAuditRubricsForProject: ({ projectId }: { projectId: string }) => ({
      queryKey: ['auditRubric', 'all', projectId] as const,
      queryFn: () => getAuditRubricsForProject(projectId),
    }),
    getAuditRubric: ({ auditRubricId }: { auditRubricId: string }) => ({
      queryKey: ['auditRubric', 'auditRubric', auditRubricId] as const,
      queryFn: () => getAuditRubric(auditRubricId),
    }),
    getLiveRubricForProject: ({ projectId }: { projectId: string }) => ({
      queryKey: ['auditRubric', 'liveRubric', projectId] as const,
      queryFn: () => getLiveRubricForProject(projectId),
    }),
    getAndValidateTaskTemplate: ({ projectId }: { projectId: string }) => ({
      queryKey: ['auditRubric', 'taskTemplate', projectId] as const,
      queryFn: () => getAndValidateTaskTemplate(projectId),
    }),
    create: ({
      projectId,
      requestData,
    }: {
      projectId: string;
      requestData: CreateAuditRubricBody;
    }) => ({
      mutationKey: ['auditRubric', 'create', projectId] as const,
      mutationFn: () => createAuditRubric(projectId, requestData),
    }),
    edit: ({ rubricId, requestData }: { rubricId: string; requestData: EditAuditRubricBody }) => ({
      mutationKey: ['auditRubric', 'edit', rubricId] as const,
      mutationFn: () => editAuditRubric(rubricId, requestData),
    }),
    rename: ({ rubricId, requestData }: { rubricId: string; requestData: { name: string } }) => ({
      mutationKey: ['auditRubric', 'rename', rubricId] as const,
      mutationFn: () => editAuditRubric(rubricId, requestData),
    }),
    updateStatus: ({
      projectId,
      rubricId,
      requestData,
    }: {
      projectId: string;
      rubricId: string;
      requestData: UpdateAuditRubricStatusBody;
    }) => ({
      mutationKey: ['auditRubric', 'updateStatus', rubricId] as const,
      mutationFn: () => updateAuditRubricStatus(projectId, rubricId, requestData),
    }),
  },
};

export function useGetAuditRubricsForProject(
  projectId: string,
): UseQueryResult<AuditRubricInfo[], Error> {
  return useQuery<AuditRubricInfo[], Error>({
    ...queryFactory.auditRubric.getAuditRubricsForProject({ projectId }),
    enabled: !!projectId,
  });
}

export function useGetAuditRubric(auditRubricId?: string): UseQueryResult<AuditRubric, Error> {
  return useQuery<AuditRubric, Error>({
    ...queryFactory.auditRubric.getAuditRubric({ auditRubricId: auditRubricId! }),
    enabled: !!auditRubricId,
  });
}

export function useGetLiveRubricForProject(projectId?: string): UseQueryResult<AuditRubric, Error> {
  return useQuery<AuditRubric, Error>({
    ...queryFactory.auditRubric.getLiveRubricForProject({ projectId: projectId! }),
    enabled: !!projectId,
  });
}

export function useCreateAuditRubric(
  projectId: string,
): UseMutationResult<AuditRubric, Error, CreateAuditRubricBody> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (requestData: CreateAuditRubricBody) =>
      queryFactory.auditRubric.create({ projectId, requestData }).mutationFn(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['auditRubric', 'all', projectId] });
      toast.success('Audit rubric created successfully');
    },
    onError: (error: any) => {
      const message = error.response?.message || error.message || 'Failed to create audit rubric';
      toast.error(message);
    },
  });
}

export function useUpdateAuditRubricStatus(
  projectId: string,
): UseMutationResult<
  AuditRubric,
  Error,
  { rubricId: string; requestData: UpdateAuditRubricStatusBody }
> {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      rubricId,
      requestData,
    }: {
      rubricId: string;
      requestData: UpdateAuditRubricStatusBody;
    }) => queryFactory.auditRubric.updateStatus({ projectId, rubricId, requestData }).mutationFn(),

    onError: (error: any) => {
      const message =
        error.response?.message || error.message || 'Failed to update audit rubric status';
      toast.error(message);
    },

    onSuccess: () => {
      toast.success('Audit rubric status updated successfully');
    },

    onSettled: (_data, _error, { rubricId }) => {
      queryClient.invalidateQueries({ queryKey: ['auditRubric', 'all', projectId] });
      queryClient.invalidateQueries({ queryKey: ['auditRubric', 'auditRubric', rubricId] });
    },
  });
}

/**
 * Updates audit rubric status with optimistic updates for responsive UI.
 * Used for approval/rejection decisions where immediate feedback is important.
 */
export function useSendApprovalDecision(): UseMutationResult<
  AuditRubric,
  Error,
  { projectId: string; rubricId: string; requestData: UpdateAuditRubricStatusBody }
> {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      projectId,
      rubricId,
      requestData,
    }: {
      projectId: string;
      rubricId: string;
      requestData: UpdateAuditRubricStatusBody;
    }) => queryFactory.auditRubric.updateStatus({ projectId, rubricId, requestData }).mutationFn(),

    onMutate: async ({ rubricId, requestData }) => {
      // Snapshot the previous value for rollback
      const previousData: { queryKey: any; data: any }[] = [];

      // Update all cached paginated lists
      queryClient
        .getQueryCache()
        .findAll({ queryKey: ['auditRubric', 'all'] })
        .forEach(query => {
          const oldData = queryClient.getQueryData<PaginatedAuditRubricInfo>(query.queryKey);
          if (oldData) {
            previousData.push({ queryKey: query.queryKey, data: oldData });

            // Optimistically update the rubric in the list
            const updatedData = {
              ...oldData,
              data: oldData.data.map(rubric =>
                rubric.id === rubricId ? { ...rubric, ...requestData } : rubric,
              ),
            };

            queryClient.setQueryData(query.queryKey, updatedData);
          }
        });

      return { previousData };
    },

    onError: (error: any, _variables, context) => {
      // Roll back to the previous data on error
      if (context?.previousData) {
        context.previousData.forEach(({ queryKey, data }) => {
          queryClient.setQueryData(queryKey, data);
        });
      }

      const message =
        error.response?.message || error.message || 'Failed to update audit rubric status';
      toast.error(message);
    },

    onSuccess: () => {
      toast.success('Audit rubric status updated successfully');
    },

    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['auditRubric', 'all'] });
    },
  });
}

// used by Project Team in OpsHub project view
export function useEditAuditRubric(): UseMutationResult<
  AuditRubric,
  Error,
  { rubricId: string; requestData: EditAuditRubricBody }
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      rubricId,
      requestData,
    }: {
      rubricId: string;
      requestData: EditAuditRubricBody;
    }) => queryFactory.auditRubric.edit({ rubricId, requestData }).mutationFn(),
    onError: (error: any) => {
      const message = error.response?.message || error.message || 'Failed to update audit rubric';
      toast.error(message);
    },
    onSuccess: () => {
      toast.success('Audit rubric updated successfully');
    },
    onSettled: (_data, _error, { rubricId }) => {
      // Always refetch after mutation to ensure cache is in sync with server
      queryClient.invalidateQueries({ queryKey: ['auditRubric', 'auditRubric', rubricId] });
    },
  });
}

export function useRenameAuditRubric(
  projectId: string,
): UseMutationResult<AuditRubric, Error, { rubricId: string; requestData: { name: string } }> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ rubricId, requestData }: { rubricId: string; requestData: { name: string } }) =>
      queryFactory.auditRubric.rename({ rubricId, requestData }).mutationFn(),
    onError: (error: any) => {
      const message = error.response?.message || error.message || 'Failed to rename audit rubric';
      toast.error(message);
    },
    onSuccess: () => {
      toast.success('Audit rubric renamed successfully');
    },
    onSettled: (_data, _error, { rubricId }) => {
      queryClient.invalidateQueries({ queryKey: ['auditRubric', 'all', projectId] });
      queryClient.invalidateQueries({ queryKey: ['auditRubric', 'auditRubric', rubricId] });
    },
  });
}

export function useGetAuditRubrics(
  params: {
    status?: AuditRubricStatus[];
    projectId?: string;
    page?: number;
    limit?: number;
    sort?: string;
    sortOrder?: 'asc' | 'desc';
  } = {},
  enabled: boolean = true,
): UseQueryResult<PaginatedAuditRubricInfo, Error> {
  return useQuery<PaginatedAuditRubricInfo, Error>({
    ...queryFactory.auditRubric.getAuditRubrics(params),
    enabled,
    staleTime: 30 * 1000, // Consider data stale after 30 seconds
    onError: (error: any) => {
      const message = error.response?.message || error.message || 'Failed to get audit rubrics';
      toast.error(message);
    },
  });
}

export function useGetAndValidateTaskTemplate(
  projectId?: string,
): UseQueryResult<TaskTemplateData, Error> {
  return useQuery({
    ...queryFactory.auditRubric.getAndValidateTaskTemplate({ projectId: projectId! }),
    enabled: !!projectId,
    onError: (error: any) => {
      const message = error.response?.message || error.message || 'Failed to get task template';
      toast.error(message);
    },
  });
}
